import React, { useEffect, useState, useMemo } from "react";
import {
  Container, Card, CardBody, Table, Badge, Spinner, Input, Row, Col,
  Alert, Button, Modal, ModalHeader, ModalBody, ModalFooter, Label, FormGroup,
} from "reactstrap";
import { Link } from "react-router-dom";
import {
  getAllTenants, getPropertyById,
  getPropertiesByOwner, addTenantByEmail,
} from "../../../helpers/firestoreService";
import { useAuthUser } from "../../../hooks/AuthProvider";
import homeBg from "../../../assets/images/home.png";

const PAGE_SIZE = 10;

const AllTenantList = () => {
  document.title = "Tenants | Support Portal";

  const user = useAuthUser();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [propertyCache, setPropertyCache] = useState({});
  const [sortField, setSortField] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);

  // Create tenant state
  const [createModal, setCreateModal] = useState(false);
  const [properties, setProperties] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [newPropertyId, setNewPropertyId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const tenantList = await getAllTenants(user.uid);
      setTenants(tenantList);

      // Resolve property addresses for display
      const propertyIds = [...new Set(tenantList.map((t) => t.propertyId).filter(Boolean))];
      const cache = {};
      await Promise.all(
        propertyIds.map((id) =>
          getPropertyById(id).then((prop) => {
            if (prop) cache[id] = prop;
          })
        )
      );
      setPropertyCache(cache);
    } catch (e) {
      console.error("Failed to load tenants:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [user]);

  // Load properties for the create modal
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [props] = await Promise.all([
          getPropertiesByOwner(user.uid),
        ]);
        setProperties(props);
      } catch (e) {
        console.error("Failed to load properties:", e);
      }
    })();
  }, [user]);

  const openCreateModal = () => {
    setError("");
    setNewEmail("");
    setNewPropertyId("");
    setCreateModal(true);
  };

  const handleCreate = async () => {
    setError("");
    const email = newEmail.trim().toLowerCase();
    if (!email) {
      setError("An email address is required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!newPropertyId) {
      setError("Please choose the property this tenant is renting.");
      return;
    }
    setSaving(true);
    try {
      await addTenantByEmail(newPropertyId, email, {
        createdByUid: user.uid,
        existingTenants: tenants.filter((t) => t.propertyId === newPropertyId),
      });
      setCreateModal(false);
      await load();
    } catch (e) {
      console.error("Add tenant failed:", e);
      setError(e.message || "Failed to add tenant. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Tenants without a property
  const noPropertyTenants = useMemo(
    () => tenants.filter((t) => !t.propertyId),
    [tenants]
  );

  // Filter + sort
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const list = tenants.filter((t) =>
      !q ||
      (t.name || "").toLowerCase().includes(q) ||
      (t.email || "").toLowerCase().includes(q) ||
      ((t.propertyId && propertyCache[t.propertyId]?.address) || t.address || "").toLowerCase().includes(q)
    );

    list.sort((a, b) => {
      let av, bv;
      switch (sortField) {
        case "name":
          av = (a.name || "").toLowerCase();
          bv = (b.name || "").toLowerCase();
          break;
        case "email":
          av = (a.email || "").toLowerCase();
          bv = (b.email || "").toLowerCase();
          break;
        case "property":
          av = ((a.propertyId && propertyCache[a.propertyId]?.address) || a.address || "").toLowerCase();
          bv = ((b.propertyId && propertyCache[b.propertyId]?.address) || b.address || "").toLowerCase();
          break;
        case "added":
          av = a.createdAt || new Date(0);
          bv = b.createdAt || new Date(0);
          break;
        default:
          av = ""; bv = "";
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return list;
  }, [tenants, search, sortField, sortDir, propertyCache]);

  // Reset to page 1 when search/sort changes
  useEffect(() => { setPage(1); }, [search, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };

  const SortIcon = ({ field }) =>
    sortField !== field ? null : (
      <i className={`bx bx-chevron-${sortDir === "asc" ? "up" : "down"} ms-1`} />
    );

  if (loading) {
    return (
      <div className="page-content text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* ── Hero Banner ──────────────────────────────────────────────────── */}
        <div className="rounded mb-4 p-4 text-white position-relative overflow-hidden" style={{ minHeight: 180 }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${homeBg})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.18 }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #556ee6 0%, #34c38f 100%)" }} />
          <div className="position-relative" style={{ zIndex: 1 }}>
            <Row className="align-items-center">
              <Col>
                <h3 className="mb-1 text-white">Tenants</h3>
                <p className="mb-0 opacity-75">All tenants across your portals.</p>
              </Col>
              <Col xs="auto">
                <Button color="light" onClick={openCreateModal}>
                  <i className="bx bx-plus me-1" /> Add Tenant
                </Button>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col xs="auto">
                <Badge color="light" className="text-dark fs-6 px-3 py-2">
                  <i className="bx bx-user me-1" />
                  {tenants.length} Tenant{tenants.length !== 1 ? "s" : ""}
                </Badge>
              </Col>
            </Row>
          </div>
        </div>

        {/* ── No-property alert ─────────────────────────────────────── */}
        {noPropertyTenants.length > 0 && (
          <Alert color="warning" className="d-flex align-items-center gap-2 mb-4">
            <i className="bx bx-error-circle fs-5" />
            <span>
              <strong>{noPropertyTenants.length}</strong> tenant{noPropertyTenants.length !== 1 ? "s have" : " has"} no property assigned.
              Assign them from the <Link to="/properties">Properties</Link> page.
            </span>
          </Alert>
        )}

        {tenants.length === 0 ? (
          <Card>
            <CardBody className="text-center py-5">
              <i className="bx bx-user-circle text-muted" style={{ fontSize: "2.5rem" }} />
              <h5 className="text-muted mt-2">No tenants yet</h5>
              <p className="text-muted">
                Add tenants from a{" "}
                <Link to="/portals">portal's tenant section</Link>.
              </p>
            </CardBody>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardBody>
              <Row className="mb-3 align-items-center">
                <Col md={4}>
                  <Input
                    type="text"
                    placeholder="Search by name, email, portal, or property…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    bsSize="sm"
                  />
                </Col>
                <Col xs="auto" className="ms-auto text-muted small">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                </Col>
              </Row>

              {filtered.length === 0 ? (
                <p className="text-muted text-center py-3">No tenants match your search.</p>
              ) : (
                <>
                  <div className="table-responsive">
                    <Table className="table-hover mb-0">
                      <thead>
                        <tr>
                          <th style={{ cursor: "pointer" }} onClick={() => toggleSort("name")}>
                            Name <SortIcon field="name" />
                          </th>
                          <th style={{ cursor: "pointer" }} onClick={() => toggleSort("email")}>
                            Email <SortIcon field="email" />
                          </th>
                          <th style={{ cursor: "pointer" }} onClick={() => toggleSort("property")}>
                            Property <SortIcon field="property" />
                          </th>
                          <th style={{ cursor: "pointer" }} onClick={() => toggleSort("added")}>
                            Added <SortIcon field="added" />
                          </th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {paged.map((t) => (
                          <tr key={t.id}>
                            <td className="fw-semibold">{t.name || t.email || "—"}</td>
                            <td className="text-muted">{t.email || "—"}</td>
                            <td>
                              {t.propertyId && propertyCache[t.propertyId] ? (
                                <Link to={`/properties/${t.propertyId}`}>
                                  {propertyCache[t.propertyId].address}
                                </Link>
                              ) : t.address ? (
                                <span className="text-muted">{t.address}</span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td className="text-muted small">
                              {t.createdAt ? t.createdAt.toLocaleDateString() : "—"}
                            </td>
                            <td>
                              {t.propertyId && propertyCache[t.propertyId]?.portalId ? (
                                <Link
                                  to={`/tenants/${propertyCache[t.propertyId].portalId}/${t.id}`}
                                  className="btn btn-outline-primary btn-sm"
                                >
                                  View
                                </Link>
                              ) : (
                                <span className="text-muted small">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {/* ── Pagination ──────────────────────────────────────────── */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <span className="text-muted small">
                        Page {page} of {totalPages}
                      </span>
                      <div className="d-flex gap-1">
                        <Button
                          size="sm"
                          color="outline-secondary"
                          disabled={page <= 1}
                          onClick={() => setPage(1)}
                        >
                          <i className="bx bx-chevrons-left" />
                        </Button>
                        <Button
                          size="sm"
                          color="outline-secondary"
                          disabled={page <= 1}
                          onClick={() => setPage((p) => p - 1)}
                        >
                          <i className="bx bx-chevron-left" />
                        </Button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                          .reduce((acc, p, idx, arr) => {
                            if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                            acc.push(p);
                            return acc;
                          }, [])
                          .map((p, idx) =>
                            p === "..." ? (
                              <span key={`ellipsis-${idx}`} className="px-2 text-muted align-self-center">…</span>
                            ) : (
                              <Button
                                key={p}
                                size="sm"
                                color={p === page ? "primary" : "outline-secondary"}
                                onClick={() => setPage(p)}
                              >
                                {p}
                              </Button>
                            )
                          )}
                        <Button
                          size="sm"
                          color="outline-secondary"
                          disabled={page >= totalPages}
                          onClick={() => setPage((p) => p + 1)}
                        >
                          <i className="bx bx-chevron-right" />
                        </Button>
                        <Button
                          size="sm"
                          color="outline-secondary"
                          disabled={page >= totalPages}
                          onClick={() => setPage(totalPages)}
                        >
                          <i className="bx bx-chevrons-right" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        )}
        {/* ── Create Tenant Modal ──────────────────────────────────────── */}
        <Modal isOpen={createModal} toggle={() => setCreateModal(false)}>
          <ModalHeader toggle={() => setCreateModal(false)}>Add Tenant</ModalHeader>
          <ModalBody>
            {error && <Alert color="danger">{error}</Alert>}

            <p className="text-muted small">
              Enter the tenant&apos;s email address. They&apos;ll get an invitation to
              accept the next time they sign in, and their name fills in
              automatically once they do.
            </p>

            <FormGroup>
              <Label>Email Address</Label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
                placeholder="tenant@example.com"
                autoFocus
              />
            </FormGroup>
            <FormGroup>
              <Label>Property</Label>
              <Input
                type="select"
                value={newPropertyId}
                onChange={(e) => setNewPropertyId(e.target.value)}
              >
                <option value="">Select a property…</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>{p.address}</option>
                ))}
              </Input>
              <small className="text-muted">Tenant will access the portal assigned to the selected property.</small>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setCreateModal(false)}>Cancel</Button>
            <Button
              color="primary"
              disabled={!newEmail.trim() || !newPropertyId || saving}
              onClick={handleCreate}
            >
              {saving ? <Spinner size="sm" /> : "Send Invitation"}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default AllTenantList;
